import React, { useMemo, useRef, WheelEvent } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import dayjs from "dayjs";
import { Loading } from "../../Loading";
import * as S from "./MultiAxesLineChart.style";
import { ReactComponent as ImgEmpty } from "../../../../styles/assets/svg/img_empty_chart.svg";
import { ChartData, ChartOption } from "../../../../types/chart";

interface MultiAxesLineChartProps {
  chartOptions?: ChartOption[];
  chartRenderLoading: boolean;
}

export const MultiAxesLineChart = ({
  chartOptions,
  chartRenderLoading,
}: MultiAxesLineChartProps) => {
  const chartRef = useRef<ReactECharts>(null);

  const convertNumber = (value?: string | number) => {
    if (!value) return 0;
    return typeof value === "string" ? Number(value) : value;
  };

  const options = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
        },
      },
      legend: {
        itemWidth: 10,
        itemGap: 10,
        icon: "circle",
        textStyle: {
          fontSize: 10,
        },
        formatter: (value: string) => {
          return value;
        },
        // legend를 5개씩 2row로 표시하기 위한 설정
        type: "scroll",
        orient: "horizontal",
        pageButtonPosition: "end",
        pageIconSize: 12,
        pageButtonGap: 5,
        pageButtonItemGap: 5,
        pageIconColor: "#666",
        pageIconInactiveColor: "#aaa",
        pageTextStyle: {
          fontSize: 10,
        },
        padding: [0, 50, 5, 50],
        selector: false,
        selectorPosition: "end",
        inactiveColor: "#ccc",
      },
      xAxis: {
        type: "time",
        axisLabel: {
          formatter: (value: any) => dayjs(value).format("YYYY.MM.DD"),
        },
      },
      grid: {
        containLabel: true,
        left: "5%", // 고정된 비율로 설정
        top: 25,
        right: "1%", // 고정된 비율로 설정
        bottom: 60,
      },
      yAxis: chartOptions
        ?.filter((option) => option.visible)
        ?.map((option, i) => ({
          min: option?.auto
            ? convertNumber(option?.chtMin)
            : convertNumber(option?.min),
          max: option?.auto
            ? convertNumber(option?.chtMax)
            : convertNumber(option?.max),
          type: "value",
          position: "left",
          axisPointer: {
            show: false,
            type: "line",
            snap: true,
          },
          axisLabel: {
            color: option.color,
            fontSize: "10px",
            margin: 4,
            formatter: (value: number) => {
              return value.toFixed(1); // 소수점 2자리까지 표시하여 레이블 길이 일관성 유지
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: option.color,
            },
          },
          nameLocation: "end",
          nameTextStyle: {
            align: "left",
            fontWeight: "bold",
            fontSize: "10px",
            padding: [0, 0, i * 10, 0],
          },
          offset: i * 30,
        })),
      dataZoom: [
        // x축 zoom/pan
        {
          type: "inside",
          xAxisIndex: 0,
          minSpan: 2, // 최소 줌 배율 5%
          maxSpan: 100, // 최대 줌 배율 100%
          moveOnMouseMove: true,
          preventDefaultMouseMove: true,
          rangeMode: ["value", "value"],
          zoomLock: false,
          throttle: 100,
        },
        // y축 zoom/pan (Ctrl키 사용 시)
        {
          type: "inside",
          yAxisIndex: chartOptions
            ?.filter((option) => option.visible)
            ?.map((_, i) => i),
          minSpan: 15, // 최소 줌 배율 15%
          maxSpan: 100, // 최대 줌 배율 100%
          moveOnMouseMove: true,
          preventDefaultMouseMove: true,
          rangeMode: ["value", "value"],
          zoomLock: false,
          throttle: 100,
        },
        // x축 슬라이더 (옵션)
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "filter",
          realtime: true,
          minSpan: 2, // 최소 줌 배율 5%
          maxSpan: 90, // 최대 줌 배율 70%
          bottom: 10,
          height: 20,
        },
      ],
      series: chartOptions
        ?.filter((option) => option.visible)
        ?.map((option, i) => ({
          name: option.name,
          smooth: true,
          type: "line",
          sampling: "lttb", // 또는 'average'
          animation: false,
          symbol: "none",
          progressive: 5000, // 🔹 progressive rendering
          showSymbol: false, // 🔹 점(마커) 제거로 렌더링 최소화
          large: true, // 🔹 대량 데이터 모드
          data: option.data.map((data: ChartData) => [
            new Date(data.time),
            data.value,
          ]),
          itemStyle: { color: option.color },
          yAxisIndex: i,
        })),
    }),
    [chartOptions]
  );
  console.log(
    "@@ options: ",
    chartOptions
      ?.filter((option) => option.visible)
      ?.map((option, i) => ({
        name: option.name,
        smooth: true,
        type: "line",
        sampling: "lttb", // 또는 'average'
        animation: false,
        symbol: "none",
        progressive: 5000, // 🔹 progressive rendering
        showSymbol: false, // 🔹 점(마커) 제거로 렌더링 최소화
        large: true, // 🔹 대량 데이터 모드
        data: option.data.map((data: ChartData) => {
          // time이 문자열이고 value가 숫자인 형태는 정상적인 구조입니다.
          // 하지만 데이터가 없거나 value가 null/undefined인 경우를 처리합니다.
          if (!data || data.value === null || data.value === undefined) {
            return [new Date(data?.time || new Date()), null];
          }
          return [new Date(data.time), data.value];
        }),
        itemStyle: { color: option.color },
        yAxisIndex: i,
      }))
  );

  // 마우스 휠 이벤트 핸들러
  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!chartRef.current) return;

    const chartInstance = chartRef.current.getEchartsInstance();
    const dom = chartInstance.getDom();
    const rect = dom.getBoundingClientRect();

    // Ctrl + 마우스 휠: Y축 줌
    if (e.ctrlKey) {
      const mouseY = e.clientY - rect.top;
      const mouseYRatio = mouseY / rect.height;
      const currentOption: any = chartInstance.getOption();
      const dz = currentOption.dataZoom && currentOption.dataZoom[1];
      if (!dz) return;

      let currentStart = dz.start as number;
      let currentEnd = dz.end as number;
      const currentRange = currentEnd - currentStart;
      const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9; // 줌 인/아웃 배율 조정
      const newRange = currentRange * zoomFactor;
      const newStart = currentStart + (currentRange - newRange) * mouseYRatio;
      const newEnd = newStart + newRange;

      chartInstance.dispatchAction({
        type: "dataZoom",
        dataZoomIndex: 1,
        start: Math.max(0, newStart),
        end: Math.min(100, newEnd),
      });

      chartInstance.resize();
    }
    // 마우스 휠: X축 줌
    else {
      const mouseX = e.clientX - rect.left;
      const mouseRatio = mouseX / rect.width;
      const currentOption: any = chartInstance.getOption();
      const dz = currentOption.dataZoom && currentOption.dataZoom[0];
      if (!dz) return;

      let currentStart = dz.start as number;
      let currentEnd = dz.end as number;
      const currentRange = currentEnd - currentStart;
      const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9; // 줌 인/아웃 배율 조정
      const newRange = currentRange * zoomFactor;
      const newStart = currentStart + (currentRange - newRange) * mouseRatio;
      const newEnd = newStart + newRange;

      chartInstance.dispatchAction({
        type: "dataZoom",
        dataZoomIndex: 0,
        start: Math.max(0, newStart),
        end: Math.min(100, newEnd),
      });
    }
  };

  if (chartRenderLoading) {
    return <Loading />;
  }

  if (!chartOptions || chartOptions.length === 0) {
    return (
      <S.EmptyText>
        <ImgEmpty />
        <span>조건을 입력하고 차트 생성을 진행해 주세요.</span>
      </S.EmptyText>
    );
  }

  if (chartOptions?.filter((option) => option.visible)?.length === 0) {
    return (
      <S.EmptyText>
        <ImgEmpty />
        <span>표시할 차트가 없습니다.</span>
      </S.EmptyText>
    );
  }

  return (
    <S.MultiAxesLineChart onWheel={handleWheel}>
      <ReactECharts
        ref={chartRef}
        key={JSON.stringify(chartOptions)}
        notMerge={true}
        echarts={echarts}
        option={options}
        opts={{ renderer: "svg" }}
      />
    </S.MultiAxesLineChart>
  );
};
